// ============================================================================
// SALES INVOICE SERVICE (ADR-0020) — entity + BU scoped.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { salesInvoiceEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery, appendRepeatedQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, ID,
  SalesInvoiceDto, CreateSalesInvoiceRequest, UpdateSalesInvoiceRequest, SalesInvoiceQueryParams,
  DocumentLineLedgerDto, ServiceLineLedgerDto,
} from '../types';

class SalesInvoiceService {
  async getAll(params?: SalesInvoiceQueryParams): Promise<PaginatedResponse<SalesInvoiceDto>> {
    const endpoint = params ? buildEndpointWithQuery(salesInvoiceEndpoints.list(), params) : salesInvoiceEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<SalesInvoiceDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }
  async getById(id: ID): Promise<SalesInvoiceDto> {
    const response = await get<ApiResponse<SalesInvoiceDto>>(salesInvoiceEndpoints.getById(id));
    return response.data.data;
  }
  /** The income ledger each item posts to on the invoice date — shown read-only on the item line. */
  async getLineLedgers(date: string, itemIds: string[]): Promise<DocumentLineLedgerDto[]> {
    const url = appendRepeatedQuery(buildEndpointWithQuery(salesInvoiceEndpoints.lineLedgers(), { date }), 'itemIds', itemIds);
    const response = await get<ApiResponse<DocumentLineLedgerDto[]>>(url);
    return response.data.data || [];
  }
  /** The income ledger each service posts to — shown read-only on the service line. */
  async getServiceLedgers(serviceIds: string[]): Promise<ServiceLineLedgerDto[]> {
    const url = appendRepeatedQuery(salesInvoiceEndpoints.serviceLedgers(), 'serviceIds', serviceIds);
    const response = await get<ApiResponse<ServiceLineLedgerDto[]>>(url);
    return response.data.data || [];
  }
  /** The item's quantity (base unit) at the location on the date — a hint, never a block. */
  async getStockOnHand(itemId: ID, stockLocationId: ID, date: string): Promise<number> {
    const url = buildEndpointWithQuery(salesInvoiceEndpoints.stockOnHand(), { itemId, stockLocationId, date });
    const response = await get<ApiResponse<number>>(url);
    return Number(response.data.data) || 0;
  }
  async create(data: CreateSalesInvoiceRequest): Promise<SalesInvoiceDto> {
    const response = await post<ApiResponse<SalesInvoiceDto>>(salesInvoiceEndpoints.create(), data);
    return response.data.data;
  }
  async update(id: ID, data: UpdateSalesInvoiceRequest): Promise<SalesInvoiceDto> {
    const response = await put<ApiResponse<SalesInvoiceDto>>(salesInvoiceEndpoints.update(id), data);
    return response.data.data;
  }
  async remove(id: ID, rowVersion: number): Promise<void> {
    const url = buildEndpointWithQuery(salesInvoiceEndpoints.remove(id), { rowVersion });
    await del<ApiResponse<unknown>>(url);
  }
  async cancel(id: ID, rowVersion: number): Promise<void> {
    const url = buildEndpointWithQuery(salesInvoiceEndpoints.cancel(id), { rowVersion });
    await post<ApiResponse<unknown>>(url, {});
  }
}

export const salesInvoiceService = new SalesInvoiceService();
export default salesInvoiceService;
